import React from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import {
  updateConnectionView,
  updateMaximumNumberOfMonitoredItems,
  updateNodeManagerActiveView,
} from "@src/reducers/your-reducer";
import { SourceTypes } from "@common/types/server-list-types";
import sourceIcon from "@src/assets/svg/source-icon.svg";
import treeStructureIcon from "@src/assets/svg/tree-structure-icon.svg";
import conditionMonitoringIcon from "@src/assets/svg/condition-monitoring-icon.svg";
import dashboardIcon from "@src/assets/svg/dashboard-icon.svg";
import MachineSettingsIcon from "@src/assets/svg/machine-settings-icon.svg";
import shotDataTableIcon from "@src/assets/svg/short-data-table-icon.svg";
import styles from "./sources-list-row-for-gateways-list.module.scss";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import navigateTo from "@src/routes/navigator";
import { Route } from "@src/routes/routes";
import { RootState } from "@src/reducers/store";
import { EditSourceNameGatewaysList } from "../edit-source-name-gateways-list/edit-source-name-gateways-list";
import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { useI18N } from "@src/language/language-context";
import { DeviceAuthOption } from "@src/types/device-auth-option";
import { RenderIfUserPermissionsRulesMatch } from "../render-if-user-permissions-rules-match/render-if-user-permissions-rules-match";
import { TicketSupportForMachineRowIconButton } from "@src/components/atoms/ticket-support-for-machine-row-icon-button/ticket-support-for-machine-row-icon-button";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { RenderIfUserHasPermission } from "../render-if-user-has-permission/render-if-user-has-permission";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface SourcesListRowForGatewaysListPropTypes {
  gatewayData: GatewayWithSourceViewStateTypes;
  sourceData: SourceTypes;
}

const cssClassName = "sources-list-row-for-gateways-list";

export const SourcesListRowForGatewaysList = ({
  gatewayData,
  sourceData,
}: SourcesListRowForGatewaysListPropTypes) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const showOTA = useFeatureFlag("show-ota", false);
  const showShotDataTable = useFeatureFlag("shotdata-table", false);

  const texts = {
    sourceIconAltText: i18n(
      "modules.sourcesListRowForGatewaysList.sourceIconAltText",
    ),
    gatewayIconAltText: i18n(
      "modules.sourcesListRowForGatewaysList.gatewayIconAltText",
    ),
    goToTreeStructure: i18n(
      "modules.sourcesListRowForGatewaysList.goToTreeStructure",
    ),
    goToConditionMonitoring: i18n(
      "modules.sourcesListRowForGatewaysList.goToConditionMonitoring",
    ),
    goToDashboard: i18n("modules.sourcesListRowForGatewaysList.goToDashboard"),
    noSoftwareVersion: i18n(
      "modules.sourcesListRowForGatewaysList.noSoftwareVersion",
    ),
    noMachineId: i18n("modules.sourcesListRowForGatewaysList.noMachineId"),
    goToMachineSettings: i18n(
      "modules.sourcesListRowForGatewaysList.goToMachineSettings",
    ),
    goToShotDataTable: i18n(
      "modules.sourcesListRowForGatewaysList.goToShotDataTable",
    ),
  };

  const goToNodeManager = () => navigateTo(Route.NODE_MANAGER_PAGE);

  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );

  const isUserSourceManager =
    authContext?.permissions?.includes("manage-sources");

  const isUserConditionMonitoringViewer = (): boolean => {
    const allowedRoles = [
      "view-condition-monitoring",
      "manage-condition-monitoring",
    ];

    return authContext?.permissions?.some((r) => allowedRoles.includes(r));
  };

  const writeSourceInfo = (source) => {
    dispatch(
      updateConnectionView({
        source,
        state: "Connected",
        authentication: DeviceAuthOption.ANONYMOUS,
      }),
    );

    dispatch(
      updateMaximumNumberOfMonitoredItems(
        source.limits.maximum_number_of_monitored_items,
      ),
    );
  };

  const navigateToNodeManagerSection = (link) => {
    dispatch(updateNodeManagerActiveView(link));
    goToNodeManager();
  };

  const clickTreeStructureLink = (e, source) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("treeStructure");
  };

  const clickDashboardLink = (e, source) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("dashboard");
  };

  const clickConditionMonitoringLink = (e, source) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("conditionMonitoring");
  };

  const clickMachineSettingsLink = (e, source) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("machineSettings");
  };

  const clickShotDataTableLink = (e, source) => {
    e.stopPropagation();
    writeSourceInfo(source);
    navigateToNodeManagerSection("shotDataTable");
  };

  return (
    <tr
      key={sourceData.source_id}
      className={styles[`${cssClassName}__grid-row`]}
      tabIndex={0}
      role="button"
    >
      <td className={styles[`${cssClassName}__grid-cell`]}>
        <span className={cn(styles[`${cssClassName}__grid-cell-flex-wrapper`])}>
          <span
            className={cn(
              styles[`${cssClassName}__description-text`],
              styles[`${cssClassName}__description-with-icon`],
              styles[
                `${cssClassName}__grid-cell-flex-wrapper--source-name-container`
              ],
            )}
          >
            {/* <IndicatorText
              text={""}
              variant={
                sourceData.connection_state.toLowerCase() === "connected"
                  ? "green"
                  : "orange"
              }
            /> */}
            <img src={sourceIcon} alt={texts.sourceIconAltText} />
            {sourceData.connection_state !== "DISCONNECTED" &&
            isUserSourceManager ? (
              <EditSourceNameGatewaysList
                gateway={gatewayData}
                source={sourceData}
              />
            ) : (
              sourceData.name
            )}
          </span>
          {sourceData.connection_state !== "DISCONNECTED" && (
            <span
              className={cn(
                styles[
                  `${cssClassName}__grid-cell-flex-wrapper--links-outer-wrapper`
                ],
                styles[
                  `${cssClassName}__grid-cell-flex-wrapper--padding-right`
                ],
              )}
            >
              <span className={styles[`${cssClassName}__links-wrapper`]}>
                <VerticalSeparator />
                <RenderIfUserPermissionsRulesMatch
                  mode="anyInEachGroup"
                  permissions={[
                    ["view-active-nodeset", "manage-active-nodeset"],
                    "view-initial-server-nodeset",
                  ]}
                >
                  <IconButton
                    fun={(e) => clickTreeStructureLink(e, sourceData)}
                    icon={treeStructureIcon}
                    tooltip={texts.goToTreeStructure}
                  />
                </RenderIfUserPermissionsRulesMatch>
                <RenderIfUserHasPermission permission="view-dashboards">
                  <IconButton
                    fun={(e) => clickDashboardLink(e, sourceData)}
                    icon={dashboardIcon}
                    tooltip={texts.goToDashboard}
                  />
                </RenderIfUserHasPermission>
                {isUserConditionMonitoringViewer() && (
                  <IconButton
                    fun={(e) => clickConditionMonitoringLink(e, sourceData)}
                    icon={conditionMonitoringIcon}
                    tooltip={texts.goToConditionMonitoring}
                  />
                )}

                <RenderIfUserHasPermission permission="create-tickets">
                  <TicketSupportForMachineRowIconButton
                    machineName={sourceData.name}
                    machineID={sourceData.source_id}
                    machineDSN={sourceData.dsn}
                  />
                </RenderIfUserHasPermission>

                {showOTA && (
                  <RenderIfUserHasMultiplePermissions
                    permissions={[
                      "initiate-update-process",
                      "approve-update-process",
                      "manage-firmware",
                    ]}
                  >
                    <IconButton
                      fun={(e) => clickMachineSettingsLink(e, sourceData)}
                      icon={MachineSettingsIcon}
                      tooltip={texts.goToMachineSettings}
                    />
                  </RenderIfUserHasMultiplePermissions>
                )}

                {showShotDataTable && (
                  <RenderIfUserHasPermission permission="view-dashboards">
                    <IconButton
                      fun={(e) => clickShotDataTableLink(e, sourceData)}
                      icon={shotDataTableIcon}
                      tooltip={texts.goToShotDataTable}
                    />
                  </RenderIfUserHasPermission>
                )}
              </span>
            </span>
          )}
        </span>
      </td>
      <td className={styles[`${cssClassName}__grid-cell`]}>
        <span className={styles[`${cssClassName}__description-text`]}>
          {sourceData.source_id || texts.noMachineId}
        </span>
      </td>
      <td className={styles[`${cssClassName}__grid-cell`]}>
        <span className={styles[`${cssClassName}__description-text`]}>
          {sourceData.dsn}
        </span>
      </td>
      {/* <td className={styles[`${cssClassName}__grid-cell`]}>
        <IndicatorText
          text={""}
          variant={
            item.connection_state.toLowerCase() === "connected"
              ? "green"
              : "orange"
          }
        />
        <span className={styles[`${cssClassName}__description-text`]}>
          Gateway Example where is the API
        </span>
        <img src={gatewayIcon} alt={texts.gatewayIconAltText} />
      </td> */}
    </tr>
  );
};
